/**
 * ShareMenu — click-to-open share card with pre-filled deep links.
 * Uses Web Share API when available on mobile; otherwise falls back
 * to a menu with WhatsApp / Twitter/X / LinkedIn / Copy Link.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Share2, Copy, Check, Send } from "lucide-react";
import { toast } from "sonner";

export default function ShareMenu({
  url,
  title,
  summary = "",
  className = "",
  size = "md",
  testIdPrefix = "share-menu",
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const share = useCallback(async () => {
    // Prefer native Web Share on mobile — pops the OS share sheet.
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text: summary, url });
        return;
      } catch { /* user dismissed — fall through to menu */ }
    }
    setOpen((v) => !v);
  }, [title, summary, url]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — long-press the link instead");
    }
  };

  const enc = encodeURIComponent;
  const shareLinks = [
    {
      key: "whatsapp", label: "WhatsApp",
      href: `https://api.whatsapp.com/send?text=${enc(title + " — " + url)}`,
    },
    {
      key: "twitter", label: "Twitter / X",
      href: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`,
    },
    {
      key: "linkedin", label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    },
  ];

  const sizes = {
    sm: "px-3 py-1 text-xs",
    md: "px-4 py-1.5 text-sm",
    lg: "px-5 py-2.5 text-base",
  }[size] || "px-4 py-1.5 text-sm";

  return (
    <div className={`relative inline-block ${className}`} ref={menuRef} data-testid={testIdPrefix}>
      <button
        type="button"
        onClick={share}
        className={`inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:bg-slate-50 font-semibold text-slate-700 ${sizes}`}
        data-testid={`${testIdPrefix}-open`}
      >
        <Share2 className="w-3.5 h-3.5" /> Share
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-white border border-slate-200 shadow-lg z-30 overflow-hidden"
             data-testid={`${testIdPrefix}-panel`}>
          {shareLinks.map((l) => (
            <a
              key={l.key}
              href={l.href}
              target="_blank" rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
              data-testid={`${testIdPrefix}-${l.key}`}
            >
              <Send className="w-3.5 h-3.5 text-slate-400" /> {l.label}
            </a>
          ))}
          <button
            type="button"
            onClick={copy}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
            data-testid={`${testIdPrefix}-copy`}
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      )}
    </div>
  );
}
